import { Link } from "@tanstack/react-router";
import { UserRound } from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { useUserProfile } from "../profile/use-user-profile";
import { joinFields, missingProfileFields } from "./profile-prompt";

/**
 * The Me tab's way into the profile: whether the photo, job title and company
 * the attendee list shows are set, and a link to the page where they are
 * edited.
 *
 * The profile is read when this card mounts, which is when the tab opens, and
 * never again: nothing here reloads it. While it loads, and when it cannot be
 * read, the card says nothing about the fields and keeps the link, because the
 * profile page reports a real error itself. This is not the dismissible
 * "Complete your profile" prompt on the Overview, which it leaves alone.
 */
export function YourProfileCard({ uid }: { uid: string }) {
  const profile = useUserProfile(uid);
  const missing =
    profile.state.status === "success"
      ? missingProfileFields(profile.state.profile)
      : null;

  return (
    <Card data-testid="your-profile">
      <CardContent className="space-y-3 py-4">
        <p className="flex items-center gap-2 font-medium text-foreground">
          <UserRound
            className="h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          Your profile
        </p>
        {missing && (
          <p
            className="text-sm text-muted-foreground"
            data-testid="your-profile-status"
          >
            {missing.length === 0
              ? "Your photo, job title and company are set."
              : `Your profile still needs a ${joinFields(missing)}.`}
          </p>
        )}
        <Button
          asChild
          variant={missing && missing.length > 0 ? "cta" : "outline"}
          className="h-11 w-full sm:w-auto"
          data-testid="your-profile-edit"
        >
          <Link to="/profile">Edit profile</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
